import { useState } from 'react'
import { Alert, Button, Card } from '@/components/ui'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useRemoveAccount } from '@/api/account'

const destructiveButtonColor = () =>
    'border-red-600 bg-red-600 text-white hover:border-red-700 hover:bg-red-700 hover:text-white focus:ring-2 focus:ring-red-500 focus:ring-offset-2'

const RemoveAccount = () => {
    const [confirmationOpen, setConfirmationOpen] = useState(false)
    const { removeAccount, loading, error } = useRemoveAccount()

    const closeConfirmation = () => {
        if (!loading) setConfirmationOpen(false)
    }

    const confirmRemoval = async () => {
        try {
            await removeAccount()
        } catch {
            // The hook exposes the actionable error below the section copy.
        }
    }

    return (
        <Card
            className="border-red-200 dark:border-red-900"
            header={{
                content: <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">Remove Account</h2>,
                bordered: true,
            }}
        >
            <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                    Removing your account will sign you out immediately and schedule your data for deletion within 24 hours. This action cannot be undone.
                </p>

                {error && (
                    <Alert type="danger" showIcon duration={0}>
                        {error}
                    </Alert>
                )}

                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="solid"
                        customColorClass={destructiveButtonColor}
                        onClick={() => setConfirmationOpen(true)}
                    >
                        Remove Account
                    </Button>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmationOpen}
                type="danger"
                title="Remove your account?"
                cancelText="Cancel"
                confirmText={loading ? 'Removing account...' : 'Remove account'}
                closable={!loading}
                shouldCloseOnEsc={!loading}
                shouldCloseOnOverlayClick={!loading}
                confirmButtonProps={{
                    loading,
                    disabled: loading,
                    customColorClass: destructiveButtonColor,
                }}
                cancelButtonProps={{ disabled: loading }}
                onClose={closeConfirmation}
                onRequestClose={closeConfirmation}
                onCancel={closeConfirmation}
                onConfirm={() => void confirmRemoval()}
            >
                <div className="space-y-4">
                    <p className="text-gray-600 dark:text-gray-400">
                        You’ll be signed out immediately and won’t be able to sign in again. Your account and related data will be permanently deleted within 24 hours.
                    </p>
                    {error && (
                        <Alert type="danger" showIcon duration={0}>
                            {error}
                        </Alert>
                    )}
                </div>
            </ConfirmDialog>
        </Card>
    )
}

export default RemoveAccount
